// ─────────────────────────────────────────────────────────────
// InternalCompatService — 사내 호환성 테스트 결과 (v7.364)
//
// ⚠ QnapCompatService 와 [의도적으로] 분리했다.
//   저 쪽은 QNAP 사이트에서 받아 온 사본이고, 이 쪽은 우리가 직접 꽂아 본 결과다.
//   출처가 다르면 책임 소재가 다르다. 한 배열에 합쳐 놓는 순간 화면에서 구분이 사라지고,
//   "QNAP 공식 목록" 이라는 하단 문구가 거짓이 된다.
//
// 테이블: internal_compat_test (migrations/v7.364-internal-compat.sql)
// 입력 주체: 전 직원 (RLS 전면 허용, 사내 폐쇄망 전제)
// ─────────────────────────────────────────────────────────────
import { supabase } from '../supabase';
import { qnapNorm } from './QnapCompatService';

// 결과 2단계 고정. '조건부'는 만들지 않는다 — 판정 기준이 사람마다 갈려
// 결국 아무도 안 믿는 값이 된다. 단서는 note 에 적는다.
export const IC_RESULT = { OK: 'ok', FAIL: 'fail' };
export const IC_RESULT_LABEL = { ok: '호환', fail: '불가' };

// 자주 쓰는 제조사 — 입력 폼 datalist 용. 강제가 아니라 힌트다.
//   ⚠ select 로 막으면 새 제조사가 나왔을 때 입력 자체가 불가능해진다.
export const IC_BRANDS = [
  '삼성', 'SK하이닉스', 'Transcend', 'Crucial', 'Kingston',
  'ADATA', 'Micron', 'Team', 'Innodisk', 'Apacer', 'Seagate', 'WD',
];

// 빈 문자열을 그대로 보내면 숫자/불리언 컬럼에서 22P02 가 난다.
const numOrNull = (v) => {
  if (v === '' || v == null) return null;
  const n = Number(String(v).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? Math.round(n) : null;
};
const strOrNull = (v) => {
  const s = String(v == null ? '' : v).trim();
  return s ? s : null;
};

export const InternalCompatService = {
  // 한 NAS 의 사내 테스트 결과 전부 (카테고리 무관).
  //   ⚠ 카테고리별로 나눠 받지 않는다. 한 모델당 많아야 수십 건이라
  //     통째로 받아서 화면에서 거르는 편이 왕복이 적다.
  //     카테고리 탭 뱃지 숫자도 이 배열에서 바로 나온다.
  async forProduct(productName) {
    if (!productName) return [];
    const { data, error } = await supabase
      .from('internal_compat_test')
      .select('*')
      .eq('product_name', productName)
      .order('brand', { ascending: true })
      .order('capacity_gb', { ascending: false, nullsFirst: false })
      .order('model', { ascending: true });
    if (error) return null;
    return data || [];
  },

  // 저장 — id 가 있으면 수정, 없으면 신규.
  //   ⚠ upsert 를 쓰지 않는 이유: 중복 방지 인덱스가 생성 컬럼(model_norm)을 쓰는데
  //     PostgREST 의 onConflict 로 생성 컬럼을 지정하면 동작이 버전을 탄다.
  //     여기서는 중복이 났을 때 [조용한 덮어쓰기] 보다 [안내]가 맞다 —
  //     다른 사람이 이미 테스트해 둔 결과를 모르고 지우는 게 최악이다.
  async save(form) {
    const payload = {
      product_name: form.product_name,
      category_type: Number(form.category_type),
      brand: String(form.brand || '').trim(),
      model: String(form.model || '').trim(),
      model_alt: strOrNull(form.model_alt),
      capacity_gb: numOrNull(form.capacity_gb),
      ddr_gen: strOrNull(form.ddr_gen),
      speed_mhz: numOrNull(form.speed_mhz),
      form_factor: strOrNull(form.form_factor),
      is_ecc: form.is_ecc === true || form.is_ecc === 'true' ? true
            : form.is_ecc === false || form.is_ecc === 'false' ? false : null,
      result: form.result === IC_RESULT.FAIL ? IC_RESULT.FAIL : IC_RESULT.OK,
      fw_version: strOrNull(form.fw_version),
      note: strOrNull(form.note),
      tested_by: strOrNull(form.tested_by),
      tested_at: form.tested_at || null,
    };
    if (!payload.product_name) return { ok: false, error: '대상 NAS 가 없습니다.' };
    if (!payload.brand) return { ok: false, error: '제조사를 입력해 주세요.' };
    if (!payload.model) return { ok: false, error: '모델명을 입력해 주세요.' };
    if (!payload.tested_at) delete payload.tested_at;   // DB 기본값(오늘)에 맡긴다

    const q = form.id
      ? supabase.from('internal_compat_test').update(payload).eq('id', form.id).select().single()
      : supabase.from('internal_compat_test').insert(payload).select().single();
    const { data, error } = await q;
    if (error) {
      // 23505 = unique_violation → 같은 NAS·카테고리에 같은 부품이 이미 있다
      if (error.code === '23505') {
        return { ok: false, dup: true,
          error: '이 NAS 에 같은 부품이 이미 등록되어 있습니다. 기존 항목을 수정해 주세요.' };
      }
      // 42P01 = undefined_table → 마이그레이션 미실행
      if (error.code === '42P01') {
        return { ok: false, error: 'internal_compat_test 테이블이 없습니다. v7.364 마이그레이션을 먼저 실행해 주세요.' };
      }
      return { ok: false, error: error.message };
    }
    return { ok: true, row: data };
  },

  async remove(id) {
    const { error } = await supabase.from('internal_compat_test').delete().eq('id', id);
    return error ? { ok: false, error: error.message } : { ok: true };
  },

  // v7.366: 일괄 삭제.
  //   ⚠ id 를 하나씩 지우면 5건에 5회 왕복이고, 중간에 하나 실패하면
  //     [절반만 지워진] 상태가 된다. .in() 한 방이면 서버에서 한 문장으로 끝난다.
  //   ⚠ 빈 배열을 그대로 넘기면 PostgREST 가 필터 없는 DELETE 로 해석할 여지가 있다.
  //     테이블이 통째로 날아가는 사고라 여기서 먼저 막는다.
  async removeMany(ids) {
    const list = (ids || []).filter(v => v != null);
    if (!list.length) return { ok: false, error: '선택된 항목이 없습니다.' };
    const { error } = await supabase.from('internal_compat_test').delete().in('id', list);
    return error ? { ok: false, error: error.message } : { ok: true, count: list.length };
  },

  // 역방향 — 부품 모델명으로 [사내 테스트한 NAS] 를 찾는다.
  //   ⚠ QnapCompatService.nasForPart 와 달리 링크 테이블을 거치지 않는다.
  //     이 테이블은 애초에 카탈로그 제품명으로 저장되기 때문이다.
  //   ⚠ 사용자 입력을 .or()/.ilike() 에 그대로 넣지 않는다 (PostgREST 문법·LIKE 와일드카드).
  async nasForPart(q) {
    const safe = String(q || '').replace(/[^A-Za-z0-9 *-]/g, '').trim();
    if (safe.length < 3) return null;
    const { data, error } = await supabase
      .from('internal_compat_test')
      .select('product_name,brand,model,model_alt,capacity_gb,category_type,result')
      .or(`model.ilike.%${safe}%,model_alt.ilike.%${safe}%`)
      .limit(500);
    if (error) return null;
    const productNames = new Set();
    const parts = new Map();
    (data || []).forEach(r => {
      productNames.add(r.product_name);
      if (!parts.has(r.model)) {
        parts.set(r.model, {
          model: r.model, brand: r.brand, capacity_gb: r.capacity_gb,
          categoryType: r.category_type, result: r.result,
        });
      }
    });
    return { parts: [...parts.values()].slice(0, 8), productNames };
  },

  // 공식 목록 행에 [사내검증] 뱃지를 붙이기 위한 색인.
  //   ⚠ 정규화 규칙은 qnapNorm 하나로 통일한다. DB 의 model_norm 생성 컬럼도 같은 규칙이다.
  //     여기서 어긋나면 뱃지가 조용히 안 붙는다 (에러가 안 나서 더 나쁘다).
  indexByModel(rows) {
    const idx = {};
    (rows || []).forEach(r => {
      const k = qnapNorm(r.model);
      if (k) idx[k] = r;
      const a = qnapNorm(r.model_alt);
      if (a && a !== k) idx[a] = r;
    });
    return idx;
  },
};
